import React, { ButtonHTMLAttributes, ForwardedRef, forwardRef } from 'react';
import cls from 'classnames'; // --> OFF

import Link, { NONE } from '../Link/Link';
import Loader, {
  BLACK as LOADER_BLACK,
  LoaderAppearanceType,
  SMALL,
  WHITE as LOADER_WHITE,
  X_SMALL,
} from '../Loader/Loader';

import styles from './Button.module.css';

export const SIZE_XSMALL = 'xs';
export const SIZE_SMALL = 's';
export const SIZE_MEDIUM = 'm';
export const SIZE_LMEDIUM = 'ml';
export const SIZE_XLARGE = 'xl';

export const DEFAULT = 'default';
export const PRIMARY = 'primary';
export const SECONDARY = 'secondary';
export const TERTIARY = 'tertiary';
export const QUATERNARY = 'quaternary';
export const ACCENT = 'accent';
export const GHOST = 'ghost';
export const GHOST_WARNING = 'ghost-warning';
export const TRANSPARENT = 'transparent';
export const TRANSPARENT_HOVERABLE = 'transparent-hoverable';
export const BORDERED = 'bordered';
export const NEUTRAL_NOT_SO_LIGHT = 'neutral-not-so-light';
export const INERT = 'inert';
export const WHITE = 'white';
export const LINK = 'link';

export const TEXT_COLOR_DARK = 'text-color-dark';
export const TEXT_COLOR_LIGHT = 'text-color-light';

export const ACTIVE_COLOR_DEFAULT = 'active-default';
export const ACTIVE_COLOR_AMBIENT = 'active-ambient';
export const ACTIVE_COLOR_LIGHTEST = 'active-lightest';
export const ACTIVE_COLOR_PRIMARY = 'active-primary';

type ActiveColorType =
  | typeof ACTIVE_COLOR_DEFAULT
  | typeof ACTIVE_COLOR_AMBIENT
  | typeof ACTIVE_COLOR_LIGHTEST
  | typeof ACTIVE_COLOR_PRIMARY;

export const DISABLED_COLOR_DEFAULT = 'disabled-default';
export const DISABLED_COLOR_DEEP = 'disabled-deep';

type DisabledColorType = typeof DISABLED_COLOR_DEFAULT | typeof DISABLED_COLOR_DEEP;

export const LEFT_ICON_POSITION = 'left';
export const RIGHT_ICON_POSITION = 'right';

const CLASS_COLOR_MAP = {
  [DEFAULT]: styles.buttonDefault,
  [PRIMARY]: styles.buttonPrimary,
  [SECONDARY]: styles.buttonSecondary,
  [TERTIARY]: styles.buttonTertiary,
  [QUATERNARY]: styles.buttonQuaternary,
  [ACCENT]: styles.buttonAccent,
  [GHOST]: styles.buttonGhost,
  [GHOST_WARNING]: styles.buttonGhostWarning,
  [TRANSPARENT]: styles.buttonTransparent,
  [TRANSPARENT_HOVERABLE]: styles.buttonTransparentHoverable,
  [BORDERED]: styles.buttonBordered,
  [NEUTRAL_NOT_SO_LIGHT]: styles.buttonNeutralNotSoLight,
  [INERT]: styles.buttonInert,
  [WHITE]: styles.buttonWhite,
  [LINK]: styles.buttonLink,
};

const CLASS_TEXT_COLOR_MAP = {
  [TEXT_COLOR_DARK]: styles.buttonTextColorDark,
  [TEXT_COLOR_LIGHT]: styles.buttonTextColorLight,
};

const COLOR_LOADER_MAP: Partial<Record<ButtonColor, typeof LOADER_BLACK>> = {
  [WHITE]: LOADER_BLACK,
};

const CLASS_ICON_POSITION_MAP = {
  [LEFT_ICON_POSITION]: styles.buttonIconLeft,
  [RIGHT_ICON_POSITION]: styles.buttonIconRight,
};

export type ButtonColor = keyof typeof CLASS_COLOR_MAP;
export type ButtonSize =
  | typeof SIZE_XSMALL
  | typeof SIZE_SMALL
  | typeof SIZE_LMEDIUM
  | typeof SIZE_MEDIUM
  | typeof SIZE_XLARGE;

const SIZE_CLASS_MAP: Record<ButtonSize, string> = {
  [SIZE_XSMALL]: styles.btnXs,
  [SIZE_SMALL]: styles.btnS,
  [SIZE_MEDIUM]: styles.btnM,
  [SIZE_LMEDIUM]: styles.btnMl,
  [SIZE_XLARGE]: styles.btnXl,
};

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  color?: ButtonColor;
  textColor?: keyof typeof CLASS_TEXT_COLOR_MAP;
  size?: ButtonSize;
  className?: Nullable<string>;
  contentClassName?: string;
  href?: Nullable<string>;
  /*
   * `block` will make button full width of parent
   * */
  block?: boolean;
  active?: Nullable<boolean>;
  activeColor?: ActiveColorType;
  /*
   * `silent` will prevent any interaction effect on button, like: hover, focus, etc
   * */
  silent?: boolean;
  disabled?: boolean;
  disabledColor?: DisabledColorType;
  // `withIcon` will make button to Icon Button
  withIcon?: boolean;
  // `withImage` will make button to Button with image inside
  withImage?: boolean;
  /*
   * `textOverflow` will render '...' in the end of text, if text larger than button width
   * */
  textOverflow?: 'ellipsis';
  target?: string;
  form?: string;
  iconPosition?: typeof LEFT_ICON_POSITION | typeof RIGHT_ICON_POSITION;
  externalURL?: boolean;
  withoutPaddings?: boolean;
  loading?: boolean;
  loaderAppearance?: LoaderAppearanceType;
  wide?: boolean;
}

function Button(props: ButtonProps, ref: ForwardedRef<HTMLButtonElement>) {
  const {
    type = 'button',
    color = DEFAULT,
    block = false,
    silent = false,
    size = SIZE_MEDIUM,
    active = null,
    activeColor = ACTIVE_COLOR_DEFAULT,
    className,
    contentClassName,
    disabled = false,
    disabledColor = DISABLED_COLOR_DEFAULT,
    textColor,
    withIcon = false,
    withImage = false,
    iconPosition = null,
    href = null,
    externalURL = false,
    target = null,
    withoutPaddings = false,
    loading = false,
    loaderAppearance,
    textOverflow,
    wide = false,
    form,
    children,
    ...rest
  } = props;

  const btnClassName = cls(
    styles.btn,
    CLASS_COLOR_MAP[color],
    textColor ? CLASS_TEXT_COLOR_MAP[textColor] : null,
    { [styles.btnIcon]: withIcon, [styles.btnImage]: withImage },
    SIZE_CLASS_MAP[size],
    {
      [styles.btnBlock]: block,
      [styles.btnActive]: active,
      [styles.btnActiveDefault]: active && activeColor === ACTIVE_COLOR_DEFAULT,
      [styles.btnActiveAmbient]: active && activeColor === ACTIVE_COLOR_AMBIENT,
      [styles.btnActiveLightest]: active && activeColor === ACTIVE_COLOR_LIGHTEST,
      [styles.btnActivePrimary]: active && activeColor === ACTIVE_COLOR_PRIMARY,
      [styles.btnDisabled]: disabled,
      [styles.btnDisabledDeep]: disabled && disabledColor === DISABLED_COLOR_DEEP,
      [styles.btnWithoutPaddings]: withoutPaddings,
      [styles.btnSilent]: silent,
      [styles.btnTextEllipsis]: textOverflow === 'ellipsis',
      [styles.btnWide]: wide,
      [styles.btnLoading]: loading,
    },
    className,
  );

  if (active !== null) {
    rest['aria-pressed'] = active;
  }

  if (disabled) {
    rest['aria-disabled'] = true;
  }

  const content = (
    <React.Fragment>
      <span
        className={cls(
          styles.btnContentWrapper,
          iconPosition ? CLASS_ICON_POSITION_MAP[iconPosition] : null,
          { [styles.btnHideChildren]: loading },
          contentClassName,
        )}
      >
        {children}
      </span>

      {loading && (
        <Loader
          appearance={loaderAppearance ?? COLOR_LOADER_MAP[color] ?? LOADER_WHITE}
          className={styles.btnLoader}
          size={size === SIZE_XSMALL ? X_SMALL : SMALL}
        />
      )}
    </React.Fragment>
  );

  if (href) {
    if (externalURL || href.startsWith('http') || href.startsWith('mailto')) {
      return (
        <Link className={btnClassName} href={href} target={target} {...rest} appearance={NONE}>
          {content}
        </Link>
      );
    }

    return (
      <Link className={btnClassName} to={href} {...rest} appearance={NONE}>
        {content}
      </Link>
    );
  }

  return (
    <button ref={ref} className={btnClassName} form={form} type={type} {...rest}>
      {content}
    </button>
  );
}

export default forwardRef(Button);
